import { h } from 'preact';
import './content_explorer.scss';
import * as is from 'utilities/is';
import { makeUniqueId } from 'utilities/data';
import { nextTick } from 'utilities/time';
import { orderBy } from 'utilities/list';
import { captionToBookmark } from 'utilities/annotations';
import { BaseAnnotation } from '../base_annotation';
import { SearchBar, DetailPanel } from './search_bar';
import { PLAYER_CONTROL_EVENT, ANNOTATION_EVENT } from 'constants/events';
import {
    COMPOSITIONS,
    COMPOSITION_TYPES,
    ALL_STYLES,
    BEHAVIORS,
    STATES,
    BEHAVIOR_MODIFIERS
} from 'constants/annotations';

export const DEFAULT_SEARCH_PLACEHOLDER = 'Explore Content';

export class ContentExplorer extends BaseAnnotation {
    constructor(props) {
        super(props);
        this.state = this.defaultState;

        this.infoPanels = [];
        this.handlingOffClick = false;

        this.eventBus.on(ANNOTATION_EVENT.HIDE, id => {
            if (this.infoPanels.includes(id)) {
                this.onHidePanel(id);
            }
        });

        this.toggleOffClick();
    }

    toggleOffClick() {
        if (this.handlingOffClick) {
            document.body.removeEventListener('click', this.onClickOff);
        } else {
            document.body.addEventListener('click', this.onClickOff);
        }

        this.handlingOffClick = !this.handlingOffClick;
    }

    onClickOff = event => {
        if (!this.state.collapsible) {
            return;
        }

        if (this.state.searchFocus && !this.el.contains(event.target) || this.el === event.target) {
            this.resetState();

            if (this.state.pendingPlay) {
                this.setState({ pendingPlay: false });
                this.eventBus.emit(PLAYER_CONTROL_EVENT.PLAY);
            }
        }
    }

    onHidePanel(id) {
        this.eventBus.emit(ANNOTATION_EVENT.REMOVE, id);
        this.infoPanels = this.infoPanels.filter(i => i !== id);
        nextTick(() => this.toggleOffClick());
    }
    
    onKeyUp = event => {
        this.setState({
            searchValue: event.target.value.toLowerCase()
        }, () => {
            if (this.state.searchValue) {
                this.eventBus.emit(PLAYER_CONTROL_EVENT.PAUSE);

                if (is.adjacentTarget(this.props.target) && !this.state.targetExpanded) {
                    this.setState({
                        targetExpanded: true
                    }, () => {
                        this.eventBus.emit(ANNOTATION_EVENT.EXPAND_TARGET, this.props.target);
                    });
                }
            } else if (this.state.targetExpanded) {
                this.setState({
                    targetExpanded: false
                }, () => {
                    this.eventBus.emit(ANNOTATION_EVENT.CONTRACT_TARGET, this.props.target);
                });
            }
        });
    }
    
    onFocusChange = (state, event) => {
        this.setState({
            searchFocus: state === 'focus' ? true : false,
            expanded: state === 'focus' ? true : false
        });
    }
    
    onSelectItem = selectedItem => {
        if (selectedItem) {
            this.eventBus.emit(PLAYER_CONTROL_EVENT.PAUSE);
            this.loadInfoPanel(selectedItem);
            this.toggleOffClick();
        }
    }

    onExpandResults = event => {
        this.setState({
            expandResults: true
        });

        event.stopPropagation();
    }
    
    goToTime = time => {
        if (this.state.collapsible) {
            this.resetState();
        }

        this.eventBus.emit(PLAYER_CONTROL_EVENT.CURRENT_TIME, time);
        this.eventBus.emit(PLAYER_CONTROL_EVENT.PLAY);

        if (is.adjacentTarget(this.props.target) && this.state.targetExpanded) {
            this.setState({
                ...this.defaultState
            }, () => {
                this.eventBus.emit(ANNOTATION_EVENT.CONTRACT_TARGET);
            });
        }
    }

    resetState() {
        this.setState(this.defaultState);
    }
    
    render() {
        const searchArgs = {
            placeholder: this.placeholder,
            onKeyUp: this.onKeyUp,
            onFocusChange: this.onFocusChange,
            bookmarks: this.bookmarks,
            results: this.results,
            searchValue: this.state.searchValue,
            selectedItem: this.state.selectedItem,
            onSelectItem: this.onSelectItem,
            focused: this.state.searchFocus,
            goToTime: this.goToTime,
            maxResults: this.maxResults,
            onExpandResults: this.onExpandResults,
            getElementStyles: this.getElementStyles.bind(this)
        };
        
        return (
            <div className="content-explorer" style={ this.getElementStyles(COMPOSITION_TYPES.ROOT) } ref={ el => this.el = el } data-has-search={ !!this.state.searchValue }>
                <SearchBar { ...searchArgs }/>
            </div>
        );
    }

    getElementStyles(compositionType) {
        const styles = super.getElementStyles(compositionType);

        if (this.state.searchValue && compositionType === COMPOSITION_TYPES.ROOT && styles.left) {
            styles.width = 'auto';
            styles.right = styles.left;
        }

        return styles;
    }

    componentWillUnmount() {
        this.toggleOffClick();
    }

    loadInfoPanel(item) {
        const id = makeUniqueId(),
            annotationConfig = {
                id,
                type: 'infopanel',
                target: 'player',
                content: item.description,
                properties: {
                    title: item.content,
                    image: item.thumbnail
                },
                styles: {
                    'compositionTypes.root': {
                        padding: '64px 96px'
                    }
                }
            };
        let existingPanel;

        while (existingPanel = this.infoPanels.pop()) {
            this.eventBus.emit(ANNOTATION_EVENT.REMOVE, existingPanel);
        }

        this.infoPanels.push(id);
        this.eventBus.emit(ANNOTATION_EVENT.ADD, annotationConfig);
    }

    get defaultState() {
        const withinAdjacent =  is.adjacentTarget(this.props.target);

        return {
            selectedItem: null,
            searchValue: '',
            targetExpanded: false,
            searchFocus: this.startFocused || withinAdjacent ? true : false,
            expanded: this.startFocused || withinAdjacent ? true : false,
            expandResults: withinAdjacent,
            collapsible: this.collapsible || withinAdjacent ? false : true,
            pendingPlay: false
        }
    }

    get results() {
        const { metadata } = this,
            matchesSearch = ({ content }) => (
                this.state.searchValue ?
                    (content || '').toLowerCase().includes(this.state.searchValue) :
                    true
            ),
            bookmarks = metadata.bookmarks.map(b => ({ ...b, content: b.description ? `${b.content} - ${b.description}` : b.content }) ).filter(matchesSearch),
            captions = metadata.captions.filter(matchesSearch),
            annotations = (
                metadata
                    .annotations
                    .filter(a => !Object.values(BEHAVIOR_MODIFIERS).includes(a.type))
                    .filter(matchesSearch)
            );

        return {
            bookmarks,
            captions,
            annotations
        }
    }

    get maxResults() {
        const DEFAULT_MAX = 3;

        return is.adjacentTarget(this.props.target) || this.state.expandResults ? this.bookmarks.length : this.options.maxResults || DEFAULT_MAX
    }

    get placeholder() {
        return this.state.searchFocus ? 'Search' : (this.props.content || DEFAULT_SEARCH_PLACEHOLDER);
    }

    get metadata() {
        return this.props.metadata || {
            bookmarks: [],
            captions: [],
            annotations: []
        };
    }

    static get composition() {
        return [
            { ...COMPOSITIONS.SIMPLE, STATES },
            ...Object
                .values(COMPOSITION_TYPES.CONTENT_EXPLORER)
                .map(v => ({
                    IDENTIFIER: v,
                    STYLES: ALL_STYLES,
                    BEHAVIORS: BEHAVIORS.CLICK,
                    STATES
                }))
        ];
    }

    static get schemaType() {
        return 'contentexplorer';
    }
}